import { useContext, useEffect, useRef, useState } from 'react';

import { useFormikContext } from 'formik';
import { omit } from 'lodash';

import { Deal } from '../gql/dealGql';
import { PaymentOptionsQueryVariables, usePaymentOptionsLazyQuery } from '../gql/generated/graphql';

import { initialPaymentOptions } from '../components/DealInfoBuyout/types';
import { DealContext } from '../libs/DealContext';

import { useBanks } from './useBanks';

type UsePaymentOptionsProps = {
  dealId?: number | null;
  creditDecisionId?: number | null;
  creditDecisionRevision?: string | null;
  creditDecisionLenderFees?: number | null;
};

export const usePaymentOptions = ({
  dealId,
  creditDecisionId,
  creditDecisionRevision,
  creditDecisionLenderFees,
}: UsePaymentOptionsProps = {}) => {
  const { deal } = useContext(DealContext);

  const { banks, loading: banksLoading } = useBanks({ onlyActive: true, deal });

  const [getPaymentOptions] = usePaymentOptionsLazyQuery();

  const {
    values: {
      financial_info,
      car: { payoff, mileage, year },
    },
  } = useFormikContext<Deal>();

  const dataInput: PaymentOptionsQueryVariables['data'] = {
    dealId,
    creditDecisionId,
    carYear: Number.parseInt(year, 10),
    carMileage: mileage,
    customerState: deal.customer.address.state,
    prequalificationCreditScore: deal.customer.prequalification?.credit_score,
    vehiclePayoff: payoff.vehicle_payoff,
    moneyDown: financial_info.money_down,
    userEnteredReserve: financial_info.user_entered_reserve,
    baseTaxAmount: financial_info.base_tax_amount,
    warrantyTaxAmount: financial_info.warranty_tax_amount,
    titleRegistrationOption: financial_info.title_registration_option,
    titleFee: financial_info.title_fee,
    newRegistrationFee: financial_info.new_registration_fee,
    registrationTransferFee: financial_info.registration_transfer_fee,
    docFee: financial_info.doc_fee,
    bank: financial_info.bank,
    bankFees: financial_info.bank_fees,
    daysToPayment: financial_info.days_to_payment,
    vscPrice: financial_info.vsc_price,
    vscCost: financial_info.vsc_cost,
    gapPrice: financial_info.gap_price,
    gapCost: financial_info.gap_cost,
    buyRate: financial_info.buy_rate,
    sellRate: financial_info.sell_rate,
    term: financial_info.term,
    tier: financial_info.selected_credit_decision?.tier,
    setterId: deal.setter_id,
    closerId: deal.closer_id,
    closer2Id: deal.closer2_id,
    banks: banks.map((bank) => omit(bank, 'slug')),
    lienholder: payoff.lienholder_name,
  };

  // Candidate changes can require a fresh server calculation even when its ID is unchanged.
  const requestKey = JSON.stringify([dataInput, creditDecisionLenderFees, creditDecisionRevision]);
  const eligible = Boolean(
    dealId &&
      creditDecisionId &&
      banks.length &&
      payoff.vehicle_payoff &&
      financial_info.days_to_payment,
  );
  const request = useRef({ key: requestKey, generation: 0 });
  if (request.current.key !== requestKey) {
    request.current = { key: requestKey, generation: request.current.generation + 1 };
  }
  const { generation } = request.current;
  const [result, setResult] = useState<{
    generation: number;
    paymentOptions?: typeof initialPaymentOptions;
  }>();

  useEffect(() => {
    if (!eligible || banksLoading) {
      return undefined;
    }

    let active = true;
    const timer = setTimeout(() => {
      getPaymentOptions({
        variables: { data: dataInput },
        fetchPolicy: 'no-cache',
        context: { queryDeduplication: false },
      })
        .then(({ data, error }) => {
          if (active) {
            setResult({
              generation,
              paymentOptions:
                !error && data?.paymentOptions
                  ? (data.paymentOptions as typeof initialPaymentOptions)
                  : undefined,
            });
          }
        })
        .catch(() => {
          if (active) {
            setResult({ generation });
          }
        });
    }, 500);
    return () => {
      active = false;
      clearTimeout(timer);
    };
    // The serialized input captures every request field without depending on object identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestKey, generation, eligible, banksLoading, getPaymentOptions]);

  const current = result?.generation === generation ? result : undefined;
  return {
    paymentOptions: current?.paymentOptions ?? initialPaymentOptions,
    isLoading: Boolean(dealId && creditDecisionId) && (banksLoading || (eligible && !current)),
    isUnavailable: !eligible || (!!current && !current.paymentOptions),
  } as const;
};
